import type { Stock } from '@/scopes/prices/domain/rooms/types'
import { differenceInDays, format } from 'date-fns'
import { PricesFilterType } from '@/scopes/prices/domain/filters/types'
import { BusRouteInfo } from '@/scopes/prices/domain/transports/types'
import { PricesState } from '@/scopes/prices/infrastructure/store'

export function getFiltersSelector(state: PricesState) {
  return {
    loading: state.getFilters.loading,
    error: state.getFilters.error,
    isEmpty: state.getFilters.isEmpty,
  }
}

export function durationFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Duration?.options ?? []
  const selectedValues = state.filterSelection.Duration ?? []
  return {
    options,
    selectedValues,
  }
}

export function roomTypeFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.RoomType?.options ?? []
  const selectedValues = state.filterSelection.RoomType ?? []
  return {
    options,
    selectedValues,
  }
}

export function transportTypeFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.TransportType?.options ?? []
  const selectedValue = state.filterSelection.TransportType ?? undefined
  return {
    options,
    selectedValue,
  }
}

export function mealplanFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Mealplan?.options ?? []
  const selectedValue = state.filterSelection.Mealplan ?? undefined
  return {
    options,
    selectedValue,
  }
}

export function monthFilterOptionsSelector(state: PricesState) {
  const options = state.filterOptions.Month?.options ?? []
  const selectedValue = state.filterSelection.Month ?? ''
  return {
    options,
    selectedValue,
  }
}

export function departureAirportSelector(state: PricesState) {
  const options = state.filterOptions.DepartureAirport?.options ?? []
  const selectedValues = state.filterSelection.DepartureAirport ?? []
  return {
    options,
    selectedValues,
  }
}

export function participantsSelector(state: PricesState) {
  const selectedValues = state.filterSelection.Participants ?? []
  return {
    selectedValues,
  }
}

export function getPricesSelector(state: PricesState) {
  return {
    loading: state.getPrices.loading,
    error: state.getPrices.error,
  }
}

export function pricesResponseSelector(state: PricesState) {
  return { ...state.pricesResponse }
}

export function alternativeFilterSelector(state: PricesState, filter: PricesFilterType) {
  return state.pricesResponse.alternativeFilters?.[filter] ?? null
}

export function selectedPriceIdSelector(state: PricesState) {
  return state.selectedPriceId
}

export function getRoomsSelector(state: PricesState) {
  return {
    loading: state.getRooms.loading,
    error: state.getRooms.error,
  }
}

export function getRoomsResponseSelector(state: PricesState) {
  return { ...state.roomsResponse }
}

export function selectedRoomInfosSelector(state: PricesState) {
  return { selectedRoomInfos: state.selectedRoomInfos }
}

function calculateUpdatedStock(currentStock: Stock, currentSelectedRooms: Record<string, string | null> | null): Stock {
  const updatedStock: Stock = { ...currentStock }

  for (const r in currentSelectedRooms) {
    const roomId = currentSelectedRooms[r]
    if (roomId && updatedStock[roomId] !== undefined) {
      updatedStock[roomId] -= 1
    }
  }
  return updatedStock
}

export function roomGroupSelector(state: PricesState, roomNumber: string) {
  const { roomGroups, stock } = getRoomsResponseSelector(state)
  const updatedStock = calculateUpdatedStock(stock, state.selectedRooms)

  const roomGroup = roomGroups?.find(group => group.roomNumber === roomNumber)

  return {
    rooms: roomGroup?.rooms || [],
    selectedRoom: state.selectedRooms?.[roomNumber],
    stock: updatedStock,
    distribution: roomGroup?.distribution,
  }
}

export function roomSelector(state: PricesState, roomNumber: string, roomId: string) {
  const { rooms, stock, selectedRoom } = roomGroupSelector(state, roomNumber)
  const { stock: apiStock } = getRoomsResponseSelector(state)
  const isSelected = selectedRoom === roomId

  return {
    room: rooms.find(room => room.id === roomId),
    stock: stock[roomId],
    apiStock: apiStock[roomId],
    isSelected,
  }
}

export function getTransportSelector(state: PricesState) {
  return {
    loading: state.getTransport.loading,
    error: state.getTransport.error,
  }
}

export function routesSelector(state: PricesState) {
  const { outboundRoutes, inboundRoutes, selectedOutboundRouteId, selectedInboundRouteId } = state
  const outboundRoute = outboundRoutes.find(route => route.id === selectedOutboundRouteId) || null
  const inboundRoute = inboundRoutes.find(route => route.id === selectedInboundRouteId) || null
  return {
    outboundRoutes,
    inboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
    outboundRoute,
    inboundRoute,
  }
}

export function busRouteSelector(state: PricesState) {
  const { busSelectorResponse, selectedOutboundBusId, selectedInboundBusId } = state

  if (!selectedOutboundBusId || !selectedInboundBusId) {
    return {
      route: {} as BusRouteInfo,
    }
  }

  return {
    route: busSelectorResponse.routes?.[selectedOutboundBusId]?.[selectedInboundBusId],
  }
}

export function busesSelector(state: PricesState) {
  const { busSelectorResponse, selectedOutboundBusId, selectedInboundBusId } = state
  const outboundBuses = busSelectorResponse?.outboundBuses || []
  const inboundBuses = busSelectorResponse?.inboundBuses || []
  const outboundBus = outboundBuses.find(bus => bus.id === selectedOutboundBusId) || null
  const inboundBus = inboundBuses.find(bus => bus.id === selectedInboundBusId) || null
  return {
    outboundBuses,
    inboundBuses,
    selectedOutboundBusId,
    selectedInboundBusId,
    outboundBus,
    inboundBus,
  }
}

export function transportResponseSelector(state: PricesState) {
  const { inboundRoute } = routesSelector(state)
  const { route } = busRouteSelector(state)
  const flightResponseTripSummary = state.flightSelectorResponse?.tripSummary || []
  const selfDriveResponseTripSummary = state.selfDriveResponse?.tripSummary || []
  const busResponseTripSummary = state.busSelectorResponse?.tripSummary || []
  const tripSummary = [
    ...flightResponseTripSummary,
    ...(inboundRoute?.tripSummary || []),
    ...selfDriveResponseTripSummary,
    ...busResponseTripSummary,
    ...(route?.tripSummary || []),
  ]
  const priceBreakdown = inboundRoute?.priceBreakdown || state.selfDriveResponse?.priceBreakdown || route?.priceBreakdown || null
  const transportType = transportTypeSelector(state)
  return { tripSummary, priceBreakdown, transportType }
}

export function paymentMethodSelector(state: PricesState) {
  if (transportTypeSelector(state) === 'self-drive') {
    return { paymentMethod: state.selfDriveResponse?.paymentMethod || null }
  }

  const { outboundRoute, inboundRoute } = routesSelector(state)
  const isExternal = outboundRoute?.flightType === 'standard' || inboundRoute?.flightType === 'standard'
  const departureDateInLessThanSixWeeks = outboundRoute && differenceInDays(new Date(outboundRoute.departureDate.rawDate), new Date()) < 42

  if (!isExternal && !departureDateInLessThanSixWeeks) {
    return { paymentMethod: state.flightSelectorResponse?.paymentMethod || null }
  }

  return { paymentMethod: null }
}

function transportTypeSelector(state: PricesState) {
  return state.flightSelectorResponse?.transportType
    || state.selfDriveResponse?.transportType
    || state.busSelectorResponse?.transportType
}

export function createBookingDraftSelector(state: PricesState) {
  return {
    loading: state.createBookingDraft.loading,
    error: state.createBookingDraft.error,
    success: state.createBookingDraft.success,
  }
}

export function isPriceTableLoadingStateSelector(state: PricesState) {
  const loadingFilters = state.getFilters.loading
  const fetchingPrices = state.getPrices.loading
  const fetchingRooms = state.getRooms.loading
  const fetchingTransport = state.getTransport.loading
  const isBookingDraftCreationPending = state.createBookingDraft.loading
  const isBookingDraftCreationSuccess = state.createBookingDraft.success

  return loadingFilters
    || fetchingPrices
    || fetchingRooms
    || fetchingTransport
    || isBookingDraftCreationPending
    || isBookingDraftCreationSuccess
}

export function isAccommodationAvailableSelector(state: PricesState) {
  return {
    isAccommodationAvailable: state.isAccommodationAvailableResponse?.isAccommodationAvailable,
    isFlightOnly: state.isAccommodationAvailableResponse?.isFlightOnly,
    transportTypes: state.isAccommodationAvailableResponse?.transportTypes,
    isFetching: state.isAccommodationAvailable.loading,
    success: state.isAccommodationAvailable.success,
  }
}

export function getPromotedPriceSelector(state: PricesState) {
  return {
    data: state.promotedPriceResponse,
    isError: state.getPromotedPrice.error,
    loading: state.getPromotedPrice.loading,
    success: state.getPromotedPrice.success,
  }
}

export function getPriceRangeSelector(state: PricesState) {
  const options = state.filterOptions.Month?.options ?? []
  const promotedPriceDate = state.promotedPriceResponse?.legacyDepartureDate?.date ?? null

  const transformedOptions = options.reduce((acc, { value, name }) => {
    const [year] = value.split('-')
    if (!acc[year]) {
      acc[year] = []
    }
    acc[year].push({
      value,
      name,
    })
    return acc
  }, {} as Record<string, { value: string, name: string }[]>)

  const minPriceDate = state.pricesResponse.prices.reduce(
    (cheapest, price) => price.averagePrice < cheapest.averagePrice ? price : cheapest,
    state.pricesResponse.prices[0],
  )?.departureDate || ''
  const selectedPriceDate = state?.selectedPriceId ? state.pricesResponse.prices.find(price => price.id === state.selectedPriceId)?.departureDate : ''
  const initialFocusDate = selectedPriceDate || promotedPriceDate || minPriceDate

  return {
    dateFrom: state.filterSelection.DateFrom,
    dateTo: state.filterSelection.DateTo,
    options: transformedOptions,
    minDatePrices: state.pricesResponse.dates[0],
    maxDatePrices: state.pricesResponse.dates[state.pricesResponse.dates.length - 1],
    minPriceDate,
    selectedPriceDate,
    promotedPriceDate,
    initialFocusDate,
  }
}

export function isFirstMonthSelector(state: PricesState) {
  return {
    isFirstMonth: (date: string) => {
      const { options } = getPriceRangeSelector(state)
      const monthValues = Object.values(options).flat()
      if (monthValues.length === 0) {
        return false
      }
      return monthValues[0].value === format(new Date(date), 'yyyy-MM')
    },
    minDatePrices: state.pricesResponse.dates[0],
  }
}
